import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import SidePanel from '../../../../../ui/SidePanel/SidePanel'
import Spinner from '../../../../../ui/Spinner/Spinner'
import { db } from '../../../../../config/firebase'
import { isNull, keyBy } from 'lodash'
import arrayMove from 'array-move'
import Switch from '../../../../../ui/Switch/Switch'
import Icon from '../../../../../ui/Icon/Icon'
import { withRouter } from 'react-router-dom'
import BrandListItem from './BrandListItem'
import BrandsArchiveSection from './BrandsArchiveSection'
import { splitBrandsByArchive } from './splitBrandsByArchive'
import styles from './BrandsPanel.module.scss'
import { sortBrands } from '../brandOrder'
import BrandsReorderList from './BrandsReorderList'
import saveBrandOrder from './saveBrandOrder'

function BrandsPanel({
  routerIdName,
  onClickPath,
  brands: providedBrands,
  isLoading = false,
  showInactiveReason = true,
  showProductsCount = false,
  selectedBrandId = '',
  canReorder = false,
  ...router
}) {
  const [loadedBrands, setLoadedBrands] = useState(null)
  const [isReorderMode, setIsReorderMode] = useState(false)
  const [isSavingOrder, setIsSavingOrder] = useState(false)
  const [pendingOrder, setPendingOrder] = useState(null)
  const [orderStatus, setOrderStatus] = useState('')
  const [loadError, setLoadError] = useState(false)
  const mounted = useRef(false)
  const savingOrder = useRef(false)
  const usesParentBrands = typeof providedBrands !== 'undefined'
  const activeId = selectedBrandId || router.match.params[routerIdName] || ''
  const sourceBrands = usesParentBrands ? providedBrands : loadedBrands
  const brands = useMemo(() => {
    if (isNull(sourceBrands)) return null
    const ordered = sortBrands(sourceBrands || [])
    if (!pendingOrder) return ordered
    const byId = keyBy(ordered, 'id')
    const pendingIds = new Set(pendingOrder)
    return [
      ...pendingOrder.map((id) => byId[id]).filter(Boolean),
      ...ordered.filter((brand) => !pendingIds.has(brand.id)),
    ]
  }, [sourceBrands, pendingOrder])

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const { active, archived } = useMemo(
    () => (isNull(brands) ? { active: [], archived: [] } : splitBrandsByArchive(brands)),
    [brands]
  )

  const isSelectedBrandHidden = useMemo(
    () => Boolean(activeId && archived.some((brand) => `${brand.id}` === `${activeId}`)),
    [activeId, archived]
  )

  useEffect(() => {
    if (usesParentBrands) return undefined

    const dbBrandsListener = db.collection('brands').onSnapshot(
      (snap) => {
        const data = []
        snap.forEach((doc) => data.push({ id: doc.id, ...doc.data() }))
        setLoadedBrands(data)
        setLoadError(false)
      },
      () => {
        setLoadError(true)
        setLoadedBrands([])
      }
    )

    return () => {
      dbBrandsListener()
    }
  }, [usesParentBrands])

  const onClick = useCallback(
    (id) => {
      router.history.push(`${onClickPath}/${id}`)
    },
    [onClickPath, router.history]
  )

  useEffect(() => {
    if (isLoading) return
    if (!activeId && !isNull(brands) && active.length > 0) {
      onClick(active[0].id)
    }
  }, [active, activeId, brands, isLoading, onClick])

  const onSortEnd = async ({ oldIndex, newIndex }) => {
    if (oldIndex === newIndex || savingOrder.current) return
    const ordered = [...arrayMove(active, oldIndex, newIndex), ...archived]
    savingOrder.current = true
    setPendingOrder(ordered.map((brand) => brand.id))
    setIsSavingOrder(true)
    setOrderStatus('Збереження порядку…')
    try {
      await saveBrandOrder(ordered)
      if (mounted.current) {
        const positions = new Map(ordered.map((brand, index) => [brand.id, index]))
        setLoadedBrands(
          (current) =>
            current?.map((brand) =>
              positions.has(brand.id) ? { ...brand, sortOrder: positions.get(brand.id) } : brand
            ) || current
        )
        setOrderStatus('Порядок збережено')
      }
    } catch (error) {
      console.error(error)
      if (mounted.current) setOrderStatus('Не вдалося зберегти порядок. Спробуйте ще раз.')
    } finally {
      savingOrder.current = false
      if (mounted.current) {
        setPendingOrder(null)
        setIsSavingOrder(false)
      }
    }
  }

  return (
    <SidePanel
      title="Бренди"
      buttons={
        canReorder && active.length > 1 ? (
          <div className={styles.reorderControl}>
            <Icon name="arrows-alt-v" weight="solid" size={12} />
            <Switch
              checked={isReorderMode}
              onChange={setIsReorderMode}
              disabled={isSavingOrder}
              ariaLabel="Змінити порядок брендів"
              title="Змінити порядок брендів"
            />
          </div>
        ) : null
      }
      content={
        <div className={styles.container}>
          {isLoading || isNull(brands) ? (
            <Spinner type="popup" />
          ) : (
            <>
              {loadError && (
                <p className={styles.status} role="alert">
                  Не вдалося завантажити бренди.
                </p>
              )}
              {orderStatus && (
                <p className={styles.status} role="status">
                  {orderStatus}
                </p>
              )}
              {canReorder && isReorderMode ? (
                <BrandsReorderList
                  brands={active}
                  activeId={activeId}
                  onClick={onClick}
                  showInactiveReason={showInactiveReason}
                  showProductsCount={showProductsCount}
                  isSaving={isSavingOrder}
                  axis="y"
                  lockAxis="y"
                  useDragHandle
                  shouldCancelStart={() => false}
                  helperClass={styles.dragHelper}
                  onSortEnd={onSortEnd}
                />
              ) : (
                active.map((brand) => (
                  <BrandListItem
                    key={brand.id}
                    brand={brand}
                    activeId={activeId}
                    onClick={onClick}
                    showInactiveReason={showInactiveReason}
                    showProductsCount={showProductsCount}
                  />
                ))
              )}

              <BrandsArchiveSection
                brands={archived}
                activeId={activeId}
                onBrandClick={onClick}
                showInactiveReason={showInactiveReason}
                showProductsCount={showProductsCount}
                defaultExpanded={isSelectedBrandHidden}
              />
            </>
          )}
        </div>
      }
    />
  )
}

export default withRouter(BrandsPanel)
